import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Plus, Trash2, Eye, X } from 'lucide-react'
import { getInvoice, createInvoice, updateInvoice, getCustomers, getProducts, getCompanySettings } from '../services/api'
import { calculateLineItemTax, calculateInvoiceTotals, placeOfSupplyConflict, getCompanyStateCode } from '../lib/gst'
import { formatCurrency, GST_STATES, toInputDate } from '../lib/formatters'
import SearchableSelect from '../components/ui/SearchableSelect'

const EMPTY_LINE = { productId: '', description: '', detailedDescription: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, activityDate: '' }

const PAYMENT_TERMS = [
  { value: 'Due on Receipt', label: 'Due on Receipt', days: 0 },
  { value: 'Net 15', label: 'Net 15', days: 15 },
  { value: 'Net 30', label: 'Net 30', days: 30 },
  { value: 'Net 45', label: 'Net 45', days: 45 },
  { value: 'Net 60', label: 'Net 60', days: 60 },
  { value: 'Custom', label: 'Custom', days: null },
]

function numberToWords(num) {
  if (num === 0) return 'Zero'
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
  const scales = ['', 'Thousand', 'Lakh', 'Crore']

  const n = Math.floor(Math.abs(num))
  if (n === 0) return 'Zero'

  // Indian numbering: ones, thousands, lakhs, crores
  const parts = []
  let rem = n
  // First group: last 3 digits (hundreds)
  parts.push(rem % 1000); rem = Math.floor(rem / 1000)
  // Subsequent groups: 2 digits each
  while (rem > 0) { parts.push(rem % 100); rem = Math.floor(rem / 100) }

  function twoDigit(n) {
    if (n < 20) return ones[n]
    return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '')
  }
  function threeDigit(n) {
    if (n === 0) return ''
    const h = Math.floor(n / 100)
    const t = n % 100
    let s = ''
    if (h) s += ones[h] + ' Hundred'
    if (t) s += (h ? ' and ' : '') + twoDigit(t)
    return s
  }

  const words = []
  parts.forEach((p, i) => {
    if (p === 0) return
    const w = i === 0 ? threeDigit(p) : twoDigit(p)
    if (w) words.unshift(w + (scales[i] ? ' ' + scales[i] : ''))
  })

  let result = 'Rupees ' + words.join(' ')
  const paise = Math.round((Math.abs(num) - n) * 100)
  if (paise > 0) result += ' and ' + twoDigit(paise) + ' Paise'
  return result
}

function addDays(dateStr, days) {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return toInputDate(d)
}

export default function InvoiceForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [form, setForm] = useState({
    customerId: '', invoiceDate: toInputDate(new Date()), dueDate: toInputDate(new Date()),
    placeOfSupply: '', currency: 'INR', paymentTerms: 'Due on Receipt',
    billToName: '', billToAddress: '', shipToName: '', shipToAddress: '', customerNote: '', notes: '',
  })
  const dueDateRef = useRef(null)
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }])
  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])
  const [company, setCompany] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    Promise.all([
      getCustomers({ take: 2000 }),
      getProducts({ take: 500 }),
      isEdit ? getInvoice(id) : null,
      getCompanySettings().catch(() => null),
    ]).then(([custRes, prodRes, invRes, compRes]) => {
      if (compRes?.data) setCompany(compRes.data)
      const custData = custRes.data
      setCustomers(Array.isArray(custData) ? custData : custData.customers || [])
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : prodRes.data.products || [])
      if (invRes) {
        const inv = invRes.data
        setForm({
          customerId: inv.customerId, invoiceDate: toInputDate(inv.date),
          dueDate: toInputDate(inv.dueDate), placeOfSupply: inv.placeOfSupply?.toString() || '',
          currency: inv.currency, paymentTerms: inv.paymentTerms || '',
          billToName: inv.billToName || '', billToAddress: inv.billToAddress || '',
          shipToName: inv.shipToName || '', shipToAddress: inv.shipToAddress || '', customerNote: inv.customerNote || '', notes: inv.notes || '',
        })
        setLineItems(inv.lineItems.map((li) => ({
          productId: li.productServiceId || '', description: li.description, detailedDescription: li.detailedDescription || '',
          hsnSac: li.hsnSacCode || '', quantity: li.qty, rate: Number(li.rate), gstRate: Number(li.gstRate),
          activityDate: li.activityDate ? toInputDate(li.activityDate) : '',
        })))
      }
    }).catch(() => {})
  }, [id, isEdit])

  // Auto-fill place of supply when customer changes
  const handleCustomerChange = (customerId) => {
    setForm((f) => ({ ...f, customerId }))
    const cust = customers.find((c) => c.id === customerId)
    if (cust?.placeOfSupply || cust?.billingStateCode) {
      setForm((f) => ({ ...f, customerId, placeOfSupply: (cust.placeOfSupply || cust.billingStateCode).toString() }))
    }
    // Auto-fill payment terms from customer default
    if (cust?.defaultPaymentTerms) {
      handlePaymentTermsChange(cust.defaultPaymentTerms)
    }
  }

  // Auto-calculate due date when payment terms change
  const handlePaymentTermsChange = (terms) => {
    const pt = PAYMENT_TERMS.find((t) => t.value === terms)
    if (pt && pt.days !== null && form.invoiceDate) {
      setForm((f) => ({ ...f, paymentTerms: terms, dueDate: addDays(f.invoiceDate, pt.days) }))
    } else {
      setForm((f) => ({ ...f, paymentTerms: terms }))
      if (terms === 'Custom') {
        setTimeout(() => dueDateRef.current?.showPicker(), 0)
      }
    }
  }

  // Recalc due date when invoice date changes
  const handleInvoiceDateChange = (date) => {
    const pt = PAYMENT_TERMS.find((t) => t.value === form.paymentTerms)
    if (pt && pt.days !== null) {
      setForm((f) => ({ ...f, invoiceDate: date, dueDate: addDays(date, pt.days) }))
    } else {
      setForm((f) => ({ ...f, invoiceDate: date }))
    }
  }

  // Auto-fill line item when product selected
  const handleProductSelect = (index, productId) => {
    const prod = products.find((p) => p.id === productId)
    if (prod) {
      updateLine(index, {
        productId, description: prod.name, hsnSac: prod.hsnSacCode || '',
        rate: Number(prod.defaultRate), gstRate: Number(prod.gstRate),
      })
    } else {
      updateLine(index, { productId })
    }
  }

  const updateLine = (index, changes) => {
    setLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, ...changes } : li)))
  }

  const addLine = () => setLineItems((prev) => [...prev, { ...EMPTY_LINE }])
  const removeLine = (index) => setLineItems((prev) => prev.filter((_, i) => i !== index))

  // Calculate totals (tax-inclusive: rate includes GST)
  const computedLines = lineItems.map((li) => {
    const inclusiveAmount = li.quantity * li.rate
    const tax = calculateLineItemTax(li.gstRate, inclusiveAmount, form.placeOfSupply)
    return { ...li, ...tax, total: inclusiveAmount }
  })
  const totals = calculateInvoiceTotals(computedLines)

  // Any taxable line means the place of supply decides CGST/SGST vs IGST. Without it
  // the invoice is silently zero-rated, which is what produced five wrong tax invoices.
  const needsPlaceOfSupply = lineItems.some((li) => Number(li.gstRate) > 0)

  // A place of supply that disagrees with the customer's own state is what put IGST on
  // O3D-2627-0176, a Karnataka-to-Karnataka supply. Said here, while the invoice is still
  // being typed; the backend refuses it at approve.
  const selectedCustomer = customers.find((c) => c.id === form.customerId)
  // The Bill To typed on THIS invoice decides it, not the customer master — an invoice
  // may legitimately be billed to a different party in a different state.
  const posConflict = needsPlaceOfSupply
    ? placeOfSupplyConflict(form.placeOfSupply, selectedCustomer, { billToAddress: form.billToAddress })
    : null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (needsPlaceOfSupply && !form.placeOfSupply) {
      setError('Place of supply is required when any line item is taxable. '
        + 'Pick the state here, or set it on the customer record so it fills in automatically.')
      return
    }
    setSaving(true)
    try {
      const payload = {
        customerId: form.customerId,
        date: form.invoiceDate,
        dueDate: form.dueDate,
        paymentTerms: form.paymentTerms,
        placeOfSupply: form.placeOfSupply ? Number(form.placeOfSupply) : null,
        currency: form.currency,
        billToName: form.billToName || null,
        billToAddress: form.billToAddress || null,
        shipToName: form.shipToName || null,
        shipToAddress: form.shipToAddress || null,
        customerNote: form.customerNote || null,
        notes: form.notes,
        lineItems: computedLines.map((li) => ({
          productServiceId: li.productId || null, description: li.description, detailedDescription: li.detailedDescription || null,
          hsnSacCode: li.hsnSac, activityDate: li.activityDate || null, qty: li.quantity, rate: li.rate, gstRate: li.gstRate,
        })),
      }
      if (isEdit) {
        await updateInvoice(id, payload)
        navigate(`/invoices/${id}`)
      } else {
        const res = await createInvoice(payload)
        navigate(`/invoices/${res.data.id}`)
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save invoice')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">
        {isEdit ? 'Edit Invoice' : 'New Invoice'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

        {/* Header fields */}
        <div className="card grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <div className="flex items-center justify-between">
              <label className="label">Customer</label>
              <Link to="/customers/new" className="text-xs text-brand-600 hover:text-brand-700">+ New customer</Link>
            </div>
            <SearchableSelect
              options={customers.map((c) => ({ value: c.id, label: c.name }))}
              value={form.customerId}
              onChange={handleCustomerChange}
              placeholder="Select customer"
              required
            />
          </div>
          <div>
            <label className="label">Invoice Date</label>
            <input type="date" className="input" value={form.invoiceDate} onChange={(e) => handleInvoiceDateChange(e.target.value)} onKeyDown={(e) => e.preventDefault()} required />
          </div>
          <div>
            <label className="label">Due Date</label>
            <input ref={dueDateRef} type="date" className="input" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value, paymentTerms: 'Custom' })} onKeyDown={(e) => e.preventDefault()} />
          </div>
          <div>
            <label className="label">
              Place of Supply {needsPlaceOfSupply && <span className="text-red-600">*</span>}
            </label>
            <SearchableSelect
              options={GST_STATES.map((s) => ({ value: String(s.code), label: `${s.code} - ${s.name}` }))}
              value={form.placeOfSupply}
              onChange={(val) => setForm({ ...form, placeOfSupply: val })}
              placeholder="Select state"
            />
            {needsPlaceOfSupply && !form.placeOfSupply && (
              <p className="mt-1 text-xs text-red-600">
                Required — without it no GST is charged and the invoice cannot be approved.
              </p>
            )}
            {posConflict && (
              <p className="mt-1 text-xs text-amber-700">
                This supply looks like it belongs in{' '}
                {posConflict.expected} - {GST_STATES.find((st) => st.code === posConflict.expected)?.name || ''}
                {' '}per {posConflict.source}.
                {' '}This invoice will be taxed as{' '}
                {Number(form.placeOfSupply) === getCompanyStateCode() ? 'CGST + SGST' : 'IGST'}.
                {' '}Change it, or state a reason at approval.
              </p>
            )}
          </div>
          <div>
            <label className="label">Currency</label>
            <select className="select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </div>
          <div>
            <label className="label">Payment Terms</label>
            <select className="select" value={form.paymentTerms} onChange={(e) => handlePaymentTermsChange(e.target.value)}>
              {PAYMENT_TERMS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        {/* Bill To (optional override) */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">Bill To <span className="text-gray-400 font-normal">(override customer's default billing address)</span></label>
            <div className="flex gap-3">
              {!form.billToName && !form.billToAddress && form.customerId && (
                <button type="button" onClick={() => {
                  const cust = customers.find((c) => c.id === form.customerId)
                  if (cust) setForm((f) => ({ ...f, billToName: cust.name, billToAddress: [cust.billingAddress, [cust.billingCity, cust.billingState, cust.billingPincode].filter(Boolean).join(', ')].filter(Boolean).join('\n') }))
                }} className="text-xs text-brand-600 hover:text-brand-700">Copy from customer</button>
              )}
              {(form.billToName || form.billToAddress) && (
                <button type="button" onClick={() => setForm((f) => ({ ...f, billToName: '', billToAddress: '' }))} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Name</label>
              <input className="input" value={form.billToName} onChange={(e) => setForm({ ...form, billToName: e.target.value })} placeholder="Leave blank to use customer's name" />
            </div>
            <div>
              <label className="label">Billing Address</label>
              <textarea className="input" rows={2} value={form.billToAddress} onChange={(e) => setForm({ ...form, billToAddress: e.target.value })} placeholder="Leave blank to use customer's default address" />
            </div>
          </div>
        </div>

        {/* Ship To (optional) */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">Ship To <span className="text-gray-400 font-normal">(if different from billing address)</span></label>
            {!form.shipToName && !form.shipToAddress && (
              <button type="button" onClick={() => {
                const cust = customers.find((c) => c.id === form.customerId)
                if (cust) setForm((f) => ({ ...f, shipToName: cust.name, shipToAddress: [cust.shippingAddress || cust.billingAddress, [cust.shippingCity || cust.billingCity, cust.shippingState || cust.billingState, cust.shippingPincode || cust.billingPincode].filter(Boolean).join(', ')].filter(Boolean).join('\n') }))
              }} className="text-xs text-brand-600 hover:text-brand-700">Copy from customer</button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Recipient Name</label>
              <input className="input" value={form.shipToName} onChange={(e) => setForm({ ...form, shipToName: e.target.value })} placeholder="Name / Hospital / Clinic" />
            </div>
            <div>
              <label className="label">Shipping Address</label>
              <textarea className="input" rows={2} value={form.shipToAddress} onChange={(e) => setForm({ ...form, shipToAddress: e.target.value })} placeholder="Full address including city, state, pincode" />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="card !p-0 overflow-hidden">
          <div className="px-6 pt-5 pb-3 border-b border-gray-100">
            <h2 className="text-lg font-medium text-gray-900">Line Items</h2>
          </div>

          {/* Table header */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="pl-6 pr-2 py-3 font-medium w-[200px]">Product / Service</th>
                  <th className="px-2 py-3 font-medium">Description</th>
                  <th className="px-2 py-3 font-medium w-[100px]">HSN/SAC</th>
                  <th className="px-2 py-3 font-medium w-[80px] text-center">Qty</th>
                  <th className="px-2 py-3 font-medium w-[120px] text-right">Rate (incl.)</th>
                  <th className="px-2 py-3 font-medium w-[90px]">GST %</th>
                  <th className="px-2 py-3 font-medium w-[110px] text-right">Amount</th>
                  <th className="px-2 pr-6 py-3 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <React.Fragment key={i}>
                    <tr className="border-b border-gray-50 hover:bg-gray-50/30 transition-colors">
                      <td className="pl-6 pr-2 py-2.5">
                        <select className="select text-sm" value={li.productId} onChange={(e) => handleProductSelect(i, e.target.value)}>
                          <option value="">Select...</option>
                          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2.5">
                        <input className="input text-sm" value={li.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder="Item name" required />
                      </td>
                      <td className="px-2 py-2.5">
                        <input className="input text-sm font-mono" value={li.hsnSac} onChange={(e) => updateLine(i, { hsnSac: e.target.value })} placeholder="Code" />
                      </td>
                      <td className="px-2 py-2.5">
                        <input type="number" min="1" className="input text-sm text-center" value={li.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })} />
                      </td>
                      <td className="px-2 py-2.5">
                        <input type="number" min="0" step="0.01" className="input text-sm text-right" value={li.rate} onChange={(e) => updateLine(i, { rate: Number(e.target.value) })} placeholder="0.00" />
                      </td>
                      <td className="px-2 py-2.5">
                        <select className="select text-sm" value={li.gstRate} onChange={(e) => updateLine(i, { gstRate: Number(e.target.value) })}>
                          <option value={0}>0%</option>
                          <option value={5}>5%</option>
                          <option value={12}>12%</option>
                          <option value={18}>18%</option>
                          <option value={28}>28%</option>
                        </select>
                      </td>
                      <td className="px-2 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">
                        {formatCurrency(computedLines[i]?.total || 0, form.currency)}
                      </td>
                      <td className="px-2 pr-6 py-2.5 text-center">
                        {lineItems.length > 1 && (
                          <button type="button" onClick={() => removeLine(i)} className="p-1 text-gray-300 hover:text-red-500 transition-colors rounded" title="Remove line">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Per-item detailed description */}
                    <tr className="border-b border-gray-100">
                      <td className="pl-6 pr-2 pb-3" />
                      <td colSpan={6} className="px-2 pb-3">
                        <textarea
                          className="input text-xs resize-none"
                          rows={li.detailedDescription ? 2 : 1}
                          value={li.detailedDescription}
                          onChange={(e) => updateLine(i, { detailedDescription: e.target.value })}
                          placeholder="Detailed description / specs / scope of work for this item..."
                          onFocus={(e) => { if (e.target.rows < 2) e.target.rows = 2 }}
                          onBlur={(e) => { if (!e.target.value) e.target.rows = 1 }}
                        />
                      </td>
                      <td />
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-3 border-t border-gray-100">
            <button type="button" onClick={addLine} className="btn-ghost flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700">
              <Plus className="h-4 w-4" /> Add Line Item
            </button>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="card w-80">
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span className="text-gray-700">{formatCurrency(totals.subtotal, form.currency)}</span></div>
              {totals.cgst > 0 && <div className="flex justify-between text-gray-500"><span>CGST</span><span className="text-gray-700">{formatCurrency(totals.cgst, form.currency)}</span></div>}
              {totals.sgst > 0 && <div className="flex justify-between text-gray-500"><span>SGST</span><span className="text-gray-700">{formatCurrency(totals.sgst, form.currency)}</span></div>}
              {totals.igst > 0 && <div className="flex justify-between text-gray-500"><span>IGST</span><span className="text-gray-700">{formatCurrency(totals.igst, form.currency)}</span></div>}
              <div className="flex justify-between font-semibold text-lg border-t border-gray-200 pt-3 mt-1">
                <span className="text-gray-900">Total</span><span className="text-brand-700">{formatCurrency(totals.total, form.currency)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Note */}
        <div className="card">
          <label className="label">Note to Customer <span className="text-gray-400 font-normal">(shown on invoice)</span></label>
          <textarea className="input" rows={2} value={form.customerNote} onChange={(e) => setForm({ ...form, customerNote: e.target.value })} placeholder="Thank you for your business! Payment due within the specified terms." />
        </div>

        {/* Internal Notes */}
        <div className="card">
          <label className="label">Internal Notes <span className="text-gray-400 font-normal">(not shown on invoice)</span></label>
          <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Private notes for your team..." />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="btn-secondary flex items-center gap-1.5"
            disabled={!lineItems.some((li) => li.description)}
          >
            <Eye className="h-4 w-4" /> Preview Invoice
          </button>
          <div className="flex gap-3">
            <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : isEdit ? 'Update Draft' : 'Create Draft'}
            </button>
          </div>
        </div>
      </form>

      {/* Preview Modal */}
      {showPreview && (
        <InvoicePreview
          company={company}
          customer={customers.find((c) => c.id === form.customerId)}
          form={form}
          lineItems={computedLines}
          totals={totals}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  )
}

/* ── Invoice Preview Modal ──────────────────────────────────────── */
function InvoicePreview({ company, customer, form, lineItems, totals, onClose }) {
  const co = company || {}
  // Derived HERE, from the lines this component is given. It used to read a binding that
  // lived in InvoiceForm, which is a different component — so opening the preview with a
  // taxable line and no place of supply threw a ReferenceError and blanked the page. That
  // is exactly the case the notice exists to cover.
  const taxUnresolved = (lineItems || []).some((li) => li.unresolved)

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[800px] mx-4 relative">
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 z-10">
          <X className="h-5 w-5" />
        </button>

        <div className="p-10 space-y-8" id="invoice-preview">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div className="flex gap-5 items-start">
              <img src="/osteo3d-logo.png" alt="Osteo3d" className="h-14 flex-shrink-0" />
              <div className="text-xs text-gray-600 space-y-0.5">
                <p className="text-sm font-semibold text-gray-900">{co.name || 'df3d creations private limited'}</p>
                {co.address && <p>{co.address}</p>}
                <p>{[co.city, co.state, co.pincode].filter(Boolean).join(', ')}</p>
                {co.phone && <p>Ph: {co.phone}</p>}
                {co.email && <p>{co.email}</p>}
                {(co.gstin || co.pan || co.cin) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 pt-1 font-mono text-[10px] text-gray-500">
                    {co.gstin && <span>GSTIN: {co.gstin}</span>}
                    {co.pan && <span>PAN: {co.pan}</span>}
                    {co.cin && <span>CIN: {co.cin}</span>}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              <h2 className="text-2xl font-bold text-brand-700 tracking-wide">INVOICE</h2>
              <p className="text-sm text-gray-400 mt-1">DRAFT</p>
              <div className="text-xs text-gray-500 mt-3 space-y-0.5">
                <p>Date: <span className="text-gray-700">{form.invoiceDate}</span></p>
                <p>Due: <span className="text-gray-700">{form.dueDate || form.invoiceDate}</span></p>
                <p>Terms: <span className="text-gray-700">{form.paymentTerms}</span></p>
                {form.placeOfSupply ? (
                  <p>Place of Supply: <span className="text-gray-700">{form.placeOfSupply} - {GST_STATES.find(s => s.code === Number(form.placeOfSupply))?.name || ''}</span></p>
                ) : taxUnresolved && (
                  <p className="text-red-600">Place of Supply not set — GST cannot be worked out</p>
                )}
              </div>
            </div>
          </div>

          {/* Bill To / Ship To */}
          <div className="border-t pt-5 grid grid-cols-2 gap-8">
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Bill To</p>
              <p className="font-semibold text-gray-900">{form.billToName || customer?.name || '—'}</p>
              <div className="text-xs text-gray-500 space-y-0.5">
                {form.billToAddress ? <p className="whitespace-pre-line">{form.billToAddress}</p> : (
                  <>
                    {customer?.billingAddress && <p>{customer.billingAddress}</p>}
                    <p>{[customer?.billingCity, customer?.billingState, customer?.billingPincode].filter(Boolean).join(', ')}</p>
                  </>
                )}
                {customer?.gstin && <p className="font-mono">GSTIN: {customer.gstin}</p>}
                {customer?.email && <p>{customer.email}</p>}
                {customer?.phone && <p>{customer.phone}</p>}
              </div>
            </div>
            {(form.shipToName || form.shipToAddress) && (
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Ship To</p>
                {form.shipToName && <p className="font-semibold text-gray-900">{form.shipToName}</p>}
                {form.shipToAddress && <p className="text-xs text-gray-500 whitespace-pre-wrap">{form.shipToAddress}</p>}
              </div>
            )}
          </div>

          {/* Line Items Table */}
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                <th className="text-left py-2.5 px-3 border-b font-semibold">#</th>
                <th className="text-left py-2.5 px-3 border-b font-semibold">Description</th>
                <th className="text-left py-2.5 px-3 border-b font-semibold">HSN/SAC</th>
                <th className="text-center py-2.5 px-3 border-b font-semibold">Qty</th>
                <th className="text-right py-2.5 px-3 border-b font-semibold">Rate</th>
                <th className="text-right py-2.5 px-3 border-b font-semibold">Tax</th>
                <th className="text-right py-2.5 px-3 border-b font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, i) => (
                <React.Fragment key={i}>
                  <tr className={li.detailedDescription ? '' : 'border-b border-gray-100'}>
                    <td className="py-2.5 px-3 text-gray-400">{i + 1}</td>
                    <td className="py-2.5 px-3 text-gray-900 font-medium">{li.description || '—'}</td>
                    <td className="py-2.5 px-3 text-gray-500 font-mono text-xs">{li.hsnSac || '—'}</td>
                    <td className="py-2.5 px-3 text-center">{li.quantity}</td>
                    <td className="py-2.5 px-3 text-right">{formatCurrency(li.rate, form.currency)}</td>
                    <td className="py-2.5 px-3 text-right text-gray-500">
                      {formatCurrency((li.cgst || 0) + (li.sgst || 0) + (li.igst || 0), form.currency)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-medium">{formatCurrency(li.total || 0, form.currency)}</td>
                  </tr>
                  {li.detailedDescription && (
                    <tr className="border-b border-gray-100">
                      <td />
                      <td colSpan={6} className="px-3 pb-2.5 text-xs text-gray-500 whitespace-pre-wrap leading-relaxed">
                        {li.detailedDescription}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{formatCurrency(totals.subtotal, form.currency)}</span></div>
              {totals.cgst > 0 && <div className="flex justify-between text-gray-500"><span>CGST</span><span>{formatCurrency(totals.cgst, form.currency)}</span></div>}
              {totals.sgst > 0 && <div className="flex justify-between text-gray-500"><span>SGST</span><span>{formatCurrency(totals.sgst, form.currency)}</span></div>}
              {totals.igst > 0 && <div className="flex justify-between text-gray-500"><span>IGST</span><span>{formatCurrency(totals.igst, form.currency)}</span></div>}
              <div className="flex justify-between font-bold text-lg border-t border-gray-300 pt-2 mt-1 text-gray-900">
                <span>Total</span><span>{formatCurrency(totals.total, form.currency)}</span>
              </div>
            </div>
          </div>

          {/* Total in words */}
          {totals.total > 0 && (
            <div className="text-xs text-gray-500 italic">
              <span className="font-medium text-gray-600">Amount in words:</span>{' '}
              {numberToWords(totals.total)} Only
            </div>
          )}

          {/* Customer Note */}
          {form.customerNote && (
            <div className="border-t pt-4">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Note</p>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{form.customerNote}</p>
            </div>
          )}

          {/* Bank Details */}
          {co.bankName && (
            <div className="border-t pt-4">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Bank Details</p>
              <div className="text-xs text-gray-600 space-y-0.5">
                <p>Bank: {co.bankName} — {co.bankBranch}</p>
                <p>Account: {co.accountName} — <span className="font-mono">{co.accountNumber}</span></p>
                {co.ifscCode && <p>IFSC: <span className="font-mono">{co.ifscCode}</span></p>}
              </div>
            </div>
          )}

          {/* Terms & Conditions */}
          <div className="border-t pt-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Terms & Conditions</p>
            <ol className="text-[10px] text-gray-500 space-y-0.5 list-decimal list-inside">
              <li>Payment is due as per the terms mentioned above.</li>
              <li>Goods once sold will not be taken back or exchanged.</li>
              <li>Subject to Bangalore jurisdiction only.</li>
              <li>Interest @ 18% p.a. will be charged on delayed payments.</li>
              <li>E. & O.E.</li>
            </ol>
          </div>

          {/* Seal & Signature */}
          <div className="flex justify-end pt-4">
            <div className="text-center">
              <img src="/df3dSeal.png" alt="Company Seal" className="h-20 w-20 object-contain mx-auto" />
              <p className="text-xs text-gray-400 mt-1">Authorised Signatory</p>
            </div>
          </div>
        </div>

        {/* Preview footer */}
        <div className="border-t px-10 py-4 bg-gray-50 rounded-b-xl flex justify-between items-center">
          <p className="text-xs text-gray-400">This is a preview. Save the draft and approve to generate the final invoice.</p>
          <button onClick={onClose} className="btn-secondary">Close Preview</button>
        </div>
      </div>
    </div>
  )
}
